/**
 * Share one network request between identical GETs that are in flight at once.
 *
 * Components that mount in the same tick each ask for the data they need, and
 * stores guard with "already loaded" flags that are still false while the
 * first request is pending — so the same GET went out two or three times.
 * This sits under `api.get`, the one path every GET takes (`apiGet`, the
 * legacy `apiCall`, and direct `api.get` callers alike).
 *
 * It is not a cache: an entry lives only until its request settles, success
 * or failure, and the next call after that reaches the network as before.
 * Callers with an AbortSignal are never shared (one caller's abort must not
 * fail another), and a write that settles forgets every entry, so a refetch
 * after a save never joins a read that started before the save landed.
 */

// Imported from its module rather than the `@shared/offline` barrel, which
// many suites mock wholesale: the account is part of every key here.
import { currentAccountId } from '@shared/offline/account';
import { registerUserScopedReset } from '@shared/services/userScopedState';

interface Entry {
  /** Settles with a pristine snapshot of the result for joiners to copy. */
  snapshot: Promise<unknown>;
  joiners: number;
}

const inFlight = new Map<string, Entry>();

/**
 * Run `request`, or join the identical one already in flight under `key`.
 *
 * The key is scoped to the signed-in account, so a request one account
 * started is never handed to whoever signs in while it is pending.
 *
 * The first caller keeps the original result; each joiner gets its own
 * structured clone, so a caller that mutates what it was handed (sorting a
 * list in place, say) cannot change another caller's data. The snapshot the
 * joiners copy from is taken in the first callback registered on the
 * request, before the first caller's code can run, and only when someone
 * joined — an unshared request pays for no clone.
 */
export function shareInFlight<T>(requestKey: string, request: () => Promise<T>): Promise<T> {
  const key = `${currentAccountId() ?? ''} ${requestKey}`;
  const pending = inFlight.get(key);
  if (pending) {
    pending.joiners += 1;
    return pending.snapshot.then(snapshot => structuredClone(snapshot) as T);
  }
  const started = request();
  const forget = () => {
    if (inFlight.get(key) === entry) inFlight.delete(key);
  };
  const entry: Entry = {
    joiners: 0,
    snapshot: started.then(
      result => {
        forget();
        return entry.joiners > 0 ? structuredClone(result) : result;
      },
      (err: unknown) => {
        forget();
        throw err;
      }
    ),
  };
  // The first caller observes the failure through `started`; with no joiners
  // the snapshot's copy of it would otherwise surface as unhandled.
  entry.snapshot.catch(() => undefined);
  inFlight.set(key, entry);
  return started;
}

/** Drop every in-flight entry, so no later GET can join a read that predates a write. */
export function forgetInFlightGets(): void {
  inFlight.clear();
}

// A sign-out ends every read the previous user started; none is joinable after.
registerUserScopedReset('inflight-gets', forgetInFlightGets);
